import { useState, useCallback, useRef, useEffect } from 'react';
import {
  playCompleteSound,
  playLineSound,
  playWrongSound,
  unlockAudio,
} from '@/pages/tmt/utils/sound';
import {
  PRACTICE1_CONFIG,
  PRACTICE2_CONFIG,
  STAGE1_CONFIG,
  STAGE2_CONFIG,
  STAGE3_CONFIG,
  TMT_ERROR_PENALTY,
  TMT_COMPLETE_DELAY_MS,
  TMT_TRANSITION_DELAY_MS,
  calculateStageScore,
  calculateFinalScore,
  classifyWrong,
  type TmtStageConfig,
  type TmtNodeSelection,
  type TmtWrongRecord,
  type TmtStageData,
  type TmtGameData,
  type CompletionStatus,
} from '@/pages/tmt/tmtConfig';

export type TmtScreen =
  | 'guide'
  | 'practice1'
  | 'practice2'
  | 'stage1'
  | 'stage2'
  | 'stage3'
  | 'result';

type StageKey = 'practice1' | 'practice2' | 'stage1' | 'stage2' | 'stage3';

const CONFIG_BY_KEY: Record<StageKey, TmtStageConfig> = {
  practice1: PRACTICE1_CONFIG,
  practice2: PRACTICE2_CONFIG,
  stage1: STAGE1_CONFIG,
  stage2: STAGE2_CONFIG,
  stage3: STAGE3_CONFIG,
};

// TMT(순서 잇기) 게임 상태 관리
// 소개 → 연습1 → 연습2 → Stage1 → Stage2 → Stage3 → 결과 순서로 진행한다.
// Stage 사이 별도 설명 없이 짧은 전환 효과 후 바로 다음 Stage를 시작한다.
// 제한시간 카운트다운 없이 경과시간을 표시하며, 각 Stage는 내부 종료 조건을 가진다.
export function useGameState() {
  const [screen, setScreen] = useState<TmtScreen>('guide');
  const [showExitModal, setShowExitModal] = useState(false);

  const [path, setPath] = useState<string[]>([]);
  const [wrongId, setWrongId] = useState<string | null>(null);
  const [completed, setCompleted] = useState(false);
  const [transitionMsg, setTransitionMsg] = useState<string | null>(null);
  const [elapsedMs, setElapsedMs] = useState(0);

  const [score, setScore] = useState(0);
  const [totalTimeMs, setTotalTimeMs] = useState(0);
  const [totalWrongCount, setTotalWrongCount] = useState(0);

  // 현재 Stage 런타임
  const stageKeyRef = useRef<StageKey>('practice1');
  const configRef = useRef<TmtStageConfig>(PRACTICE1_CONFIG);
  const pathRef = useRef<string[]>([]);
  const isActiveRef = useRef(false);
  const stageEndedRef = useRef(false);
  const wrongCountRef = useRef(0);
  const selectionsRef = useRef<TmtNodeSelection[]>([]);
  const wrongSelectionsRef = useRef<string[]>([]);
  const wrongDetailRef = useRef<TmtWrongRecord[]>([]);
  const lastCorrectNodeRef = useRef<string | null>(null);
  const maxTimeMsRef = useRef(0);
  const maxErrorsRef = useRef(0);
  const startTimeRef = useRef(0);
  const lastSelectTimeRef = useRef(0);

  // Stage 결과 보관
  const stage1ResultRef = useRef<TmtStageData | null>(null);
  const stage2ResultRef = useRef<TmtStageData | null>(null);
  const stage3ResultRef = useRef<TmtStageData | null>(null);

  // 다음 Stage 전환 함수 (순환 참조 회피용 ref)
  const advanceRef = useRef<() => void>(() => {});

  const timerRef = useRef<number | null>(null);
  const wrongTimerRef = useRef<number | null>(null);
  const completeTimerRef = useRef<number | null>(null);
  const transitionTimerRef = useRef<number | null>(null);
  const errorLimitTimerRef = useRef<number | null>(null);

  const clearTimers = useCallback(() => {
    if (timerRef.current !== null) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (wrongTimerRef.current !== null) {
      clearTimeout(wrongTimerRef.current);
      wrongTimerRef.current = null;
    }
    if (completeTimerRef.current !== null) {
      clearTimeout(completeTimerRef.current);
      completeTimerRef.current = null;
    }
    if (transitionTimerRef.current !== null) {
      clearTimeout(transitionTimerRef.current);
      transitionTimerRef.current = null;
    }
    if (errorLimitTimerRef.current !== null) {
      clearTimeout(errorLimitTimerRef.current);
      errorLimitTimerRef.current = null;
    }
  }, []);

  useEffect(() => clearTimers, [clearTimers]);

  const stopTimer = useCallback(() => {
    if (timerRef.current !== null) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  // 현재 Stage 종료 데이터 생성 (본게임 Stage만 호출)
  const buildStageData = useCallback((status: CompletionStatus): TmtStageData => {
    const cfg = configRef.current;
    const timeMs = Date.now() - startTimeRef.current;
    const wrongCount = wrongCountRef.current;
    const adjustedTimeSec = timeMs / 1000 + wrongCount * TMT_ERROR_PENALTY;
    const completed = status === 'completed';
    const stageScore = calculateStageScore(
      cfg.maxScore,
      cfg.fastTime,
      cfg.slowTime,
      adjustedTimeSec,
      pathRef.current.length,
      cfg.order.length,
      completed,
    );
    return {
      stage: stageKeyRef.current as 'stage1' | 'stage2' | 'stage3',
      completionStatus: status,
      timeMs,
      adjustedTimeSec: Math.round(adjustedTimeSec * 10) / 10,
      wrongCount,
      completedCorrectCount: pathRef.current.length,
      totalCorrectCount: cfg.order.length,
      selections: selectionsRef.current,
      wrongSelections: wrongSelectionsRef.current,
      wrongDetail: wrongDetailRef.current,
      lastCorrectNode: lastCorrectNodeRef.current,
      endedAt: new Date().toISOString(),
      stageScore: Math.round(stageScore * 10) / 10,
    };
  }, []);

  // 전체 결과 계산 후 결과 화면 이동
  const finishGame = useCallback(() => {
    stopTimer();
    const s1 = stage1ResultRef.current;
    const s2 = stage2ResultRef.current;
    const s3 = stage3ResultRef.current;

    const totalTime = (s1?.timeMs ?? 0) + (s2?.timeMs ?? 0) + (s3?.timeMs ?? 0);
    const totalWrong = (s1?.wrongCount ?? 0) + (s2?.wrongCount ?? 0) + (s3?.wrongCount ?? 0);
    const completedStageCount = [s1, s2, s3].filter(
      (s) => s?.completionStatus === 'completed',
    ).length;
    const finalScore = calculateFinalScore([
      s1?.stageScore ?? 0,
      s2?.stageScore ?? 0,
      s3?.stageScore ?? 0,
    ]);

    const gameData: TmtGameData = {
      gameType: 'tmt',
      performedAt: new Date().toISOString(),
      stage1: s1,
      stage2: s2,
      stage3: s3,
      totalTimeMs: totalTime,
      totalWrongCount: totalWrong,
      completedStageCount,
      stageScores: {
        stage1: s1?.stageScore ?? 0,
        stage2: s2?.stageScore ?? 0,
        stage3: s3?.stageScore ?? 0,
      },
      score: finalScore,
      isAborted: false,
    };

    setScore(finalScore);
    setTotalTimeMs(totalTime);
    setTotalWrongCount(totalWrong);
    console.log('TMT 본게임 데이터:', gameData);
    setScreen('result');
  }, [stopTimer]);

  // 시간 초과 / 오답 초과로 Stage 종료 (중립 전환)
  const handleStageFail = useCallback(
    (status: CompletionStatus) => {
      if (stageEndedRef.current) return;
      stageEndedRef.current = true;
      isActiveRef.current = false;
      stopTimer();

      const key = stageKeyRef.current;
      if (key === 'stage1') stage1ResultRef.current = buildStageData(status);
      else if (key === 'stage2') stage2ResultRef.current = buildStageData(status);
      else if (key === 'stage3') stage3ResultRef.current = buildStageData(status);

      // Stage 3은 바로 결과로, 그 외에는 중립 전환 후 다음 Stage
      if (key === 'stage3') {
        transitionTimerRef.current = window.setTimeout(
          () => finishGame(),
          TMT_TRANSITION_DELAY_MS,
        );
        return;
      }
      setTransitionMsg('다음 단계로 넘어갈게요.');
      transitionTimerRef.current = window.setTimeout(() => {
        setTransitionMsg(null);
        advanceRef.current();
      }, TMT_TRANSITION_DELAY_MS);
    },
    [stopTimer, buildStageData, finishGame],
  );

  // 정상 완료: 완성 연출 후 다음 Stage 전환
  const handleStageComplete = useCallback(() => {
    if (stageEndedRef.current) return;
    stageEndedRef.current = true;
    isActiveRef.current = false;
    stopTimer();
    setCompleted(true);

    const key = stageKeyRef.current;
    if (key === 'stage1') stage1ResultRef.current = buildStageData('completed');
    else if (key === 'stage2') stage2ResultRef.current = buildStageData('completed');
    else if (key === 'stage3') stage3ResultRef.current = buildStageData('completed');

    completeTimerRef.current = window.setTimeout(() => {
      advanceRef.current();
    }, TMT_COMPLETE_DELAY_MS);
  }, [stopTimer, buildStageData]);

  // Stage 시작: 런타임 초기화 + 경과시간 타이머 + 시간 제한 감시
  const beginStage = useCallback(
    (key: StageKey) => {
      const cfg = CONFIG_BY_KEY[key];
      stageKeyRef.current = key;
      configRef.current = cfg;
      maxTimeMsRef.current = cfg.maxTimeSec * 1000;
      maxErrorsRef.current = cfg.maxErrors;
      pathRef.current = [];
      wrongCountRef.current = 0;
      selectionsRef.current = [];
      wrongSelectionsRef.current = [];
      wrongDetailRef.current = [];
      lastCorrectNodeRef.current = null;
      stageEndedRef.current = false;
      isActiveRef.current = true;
      setPath([]);
      setWrongId(null);
      setCompleted(false);
      setTransitionMsg(null);
      setElapsedMs(0);
      startTimeRef.current = Date.now();
      lastSelectTimeRef.current = startTimeRef.current;

      stopTimer();
      timerRef.current = window.setInterval(() => {
        const elapsed = Date.now() - startTimeRef.current;
        setElapsedMs(elapsed);
        if (elapsed >= maxTimeMsRef.current) {
          handleStageFail('time_limit');
        }
      }, 100);
      setScreen(key as TmtScreen);
    },
    [stopTimer, handleStageFail],
  );

  // 다음 Stage 전환
  const advance = useCallback(() => {
    const key = stageKeyRef.current;
    if (key === 'stage3') {
      finishGame();
      return;
    }
    const next: Record<StageKey, StageKey> = {
      practice1: 'practice2',
      practice2: 'stage1',
      stage1: 'stage2',
      stage2: 'stage3',
      stage3: 'stage3',
    };
    beginStage(next[key]);
  }, [beginStage, finishGame]);

  useEffect(() => {
    advanceRef.current = advance;
  }, [advance]);

  // 소개 → 연습 시작 (전체 결과 초기화)
  const startPractice = useCallback(() => {
    unlockAudio();
    stage1ResultRef.current = null;
    stage2ResultRef.current = null;
    stage3ResultRef.current = null;
    beginStage('practice1');
  }, [beginStage]);

  // 노드 선택 판정 (드래그 종료 노드 기준)
  const handleSelect = useCallback(
    (nodeId: string) => {
      if (!isActiveRef.current || stageEndedRef.current) return;
      const cfg = configRef.current;
      const idx = pathRef.current.length;
      if (idx >= cfg.order.length) return;

      const expected = cfg.order[idx];
      const now = Date.now();
      const moveTime = idx === 0 ? 0 : now - lastSelectTimeRef.current;
      lastSelectTimeRef.current = now;

      if (nodeId === expected) {
        selectionsRef.current = [
          ...selectionsRef.current,
          {
            nodeId,
            isCorrect: true,
            selectedAtMs: now - startTimeRef.current,
            moveTimeMs: moveTime,
          },
        ];
        const newPath = [...pathRef.current, nodeId];
        pathRef.current = newPath;
        lastCorrectNodeRef.current = nodeId;
        setPath(newPath);

        if (newPath.length >= cfg.order.length) {
          playCompleteSound();
          handleStageComplete();
        } else {
          // 선이 이어지는 순간의 짧은 소리
          playLineSound();
        }
      } else {
        playWrongSound();
        wrongCountRef.current += 1;
        wrongSelectionsRef.current = [...wrongSelectionsRef.current, nodeId];
        selectionsRef.current = [
          ...selectionsRef.current,
          {
            nodeId,
            isCorrect: false,
            selectedAtMs: now - startTimeRef.current,
            moveTimeMs: moveTime,
          },
        ];
        if (
          stageKeyRef.current === 'stage2' ||
          stageKeyRef.current === 'stage3'
        ) {
          wrongDetailRef.current = [
            ...wrongDetailRef.current,
            classifyWrong(nodeId, cfg.ruleColor, cfg.ruleShape, cfg.nodes),
          ];
        }

        setWrongId(nodeId);
        if (wrongTimerRef.current !== null) clearTimeout(wrongTimerRef.current);
        wrongTimerRef.current = window.setTimeout(() => setWrongId(null), 350);

        // 오답 횟수 초과: 피드백을 잠깐 보여준 뒤 종료
        if (wrongCountRef.current >= maxErrorsRef.current) {
          isActiveRef.current = false;
          stopTimer();
          if (errorLimitTimerRef.current !== null) {
            clearTimeout(errorLimitTimerRef.current);
          }
          errorLimitTimerRef.current = window.setTimeout(() => {
            handleStageFail('error_limit');
          }, 600);
        }
      }
    },
    [handleStageComplete, handleStageFail, stopTimer],
  );

  const openExitModal = useCallback(() => setShowExitModal(true), []);
  const closeExitModal = useCallback(() => setShowExitModal(false), []);

  const confirmExit = useCallback(() => {
    setShowExitModal(false);
    clearTimers();
    isActiveRef.current = false;
    setScreen('guide');
  }, [clearTimers]);

  const goToNext = useCallback(() => {
    // TODO: 다음 단계(음성 인지건강 체크)로 이동
  }, []);

  return {
    screen,
    showExitModal,
    path,
    wrongId,
    completed,
    transitionMsg,
    elapsedMs,
    score,
    totalTimeMs,
    totalWrongCount,
    goToPractice: startPractice,
    handleSelect,
    openExitModal,
    closeExitModal,
    confirmExit,
    goToNext,
  };
}